import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import { MasterAuthProvider } from "@/contexts/MasterAuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "next-themes";
import MasterRoute from "@/components/master/MasterRoute";
import ScrollToTop from "@/components/ScrollToTop";
import TenantNotFound from "@/components/TenantNotFound";
import ErrorBoundary from "@/components/ErrorBoundary";
const LandingPage    = lazy(() => import("@/pages/LandingPage"));
const Privacidade    = lazy(() => import("@/pages/Privacidade"));
const Termos         = lazy(() => import("@/pages/Termos"));
const ArquiteturaLGPD  = lazy(() => import("@/pages/ArquiteturaLGPD"));
const AdminLogin     = lazy(() => import("@/pages/AdminLogin"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const Franqueado     = lazy(() => import("@/pages/Franqueado"));
const Administradoras = lazy(() => import("@/pages/Administradoras"));
const Sindicos       = lazy(() => import("@/pages/Sindicos"));
const Suporte        = lazy(() => import("@/pages/Suporte"));
const Contato        = lazy(() => import("@/pages/Contato"));
const Sobre          = lazy(() => import("@/pages/Sobre"));
const BlogIndex      = lazy(() => import("@/pages/blog/BlogIndex"));
const BlogArticle    = lazy(() => import("@/pages/blog/BlogArticle"));
const ChecklistPortaria = lazy(() => import("@/pages/ChecklistPortaria"));
const LivroOcorrencias  = lazy(() => import("@/pages/LivroOcorrencias"));
const ModeloAviso       = lazy(() => import("@/pages/ModeloAviso"));
const Encomendas        = lazy(() => import("@/pages/Encomendas"));
const DashboardPortaria = lazy(() => import("@/pages/DashboardPortaria"));
const Portaria          = lazy(() => import("@/pages/Portaria"));
const Veiculos          = lazy(() => import("@/pages/Veiculos"));
const PublicContract    = lazy(() => import("@/pages/PublicContract"));
const PublicQuote       = lazy(() => import("@/pages/PublicQuote"));
const Reservas          = lazy(() => import("@/pages/Reservas"));
const LivroDigital      = lazy(() => import("@/pages/LivroDigital"));
const Comunicados       = lazy(() => import("@/pages/Comunicados"));
// SEO programático — páginas pilar
const GestaoCondominios      = lazy(() => import("@/pages/seo/GestaoCondominios"));
const PortariaDigital        = lazy(() => import("@/pages/seo/PortariaDigital"));
const ControleEncomendas     = lazy(() => import("@/pages/seo/ControleEncomendasCondominio"));
const ControleVisitantes     = lazy(() => import("@/pages/seo/ControleVisitantesCondominio"));
const GestaoCondominiosES    = lazy(() => import("@/pages/seo/GestaoCondominiosES"));
const PortariaDigitalES      = lazy(() => import("@/pages/seo/PortariaDigitalES"));
const ControleEncomendasES   = lazy(() => import("@/pages/seo/ControleEncomendasES"));
const ControleVisitantesES   = lazy(() => import("@/pages/seo/ControleVisitantesES"));
// SEO programático — templates dinâmicos
const CidadePage             = lazy(() => import("@/pages/seo/CidadePage"));
const CidadePageES           = lazy(() => import("@/pages/seo/CidadePageES"));
const ProblemaPage           = lazy(() => import("@/pages/seo/ProblemaPage"));
const ProblemaPageES         = lazy(() => import("@/pages/seo/ProblemaPageES"));
const TipoPage               = lazy(() => import("@/pages/seo/TipoPage"));
const TipoPageES             = lazy(() => import("@/pages/seo/TipoPageES"));
const ComparacaoPage         = lazy(() => import("@/pages/seo/ComparacaoPage"));
const ComparacaoPageES       = lazy(() => import("@/pages/seo/ComparacaoPageES"));
// App do condomínio (lazy — não carregam na landing page)
const Index          = lazy(() => import("./pages/Index"));
const MoradorPacotes = lazy(() => import("./pages/MoradorPacotes"));
const AprovarAcesso  = lazy(() => import("./pages/AprovarAcesso"));
import NotFound from "./pages/NotFound";
import { useServerHealth } from "@/hooks/useServerHealth";
import { useLivePresence } from "@/hooks/useLivePresence";

// Páginas do painel master (lazy — não carregam na landing)
const MasterLogin        = lazy(() => import("@/pages/master/MasterLogin"));
const MasterLayout       = lazy(() => import("@/pages/master/MasterLayout"));
const MasterDashboard    = lazy(() => import("@/pages/master/MasterDashboard"));
const MasterCondominios  = lazy(() => import("@/pages/master/MasterCondominios"));
const MasterPlanos       = lazy(() => import("@/pages/master/MasterPlanos"));
const MasterFaturamento  = lazy(() => import("@/pages/master/MasterFaturamento"));
const MasterMoradores    = lazy(() => import("@/pages/master/MasterMoradores"));
const MasterAcessos      = lazy(() => import("@/pages/master/MasterAcessos"));
const MasterRelatorios   = lazy(() => import("@/pages/master/MasterRelatorios"));
const MasterConfiguracoes = lazy(() => import("@/pages/master/MasterConfiguracoes"));
const MasterWhatsApp      = lazy(() => import("@/pages/master/MasterWhatsApp"));
const MasterChatbot       = lazy(() => import("@/pages/master/MasterChatbot"));
const MasterLeads         = lazy(() => import("@/pages/master/MasterLeads"));
const MasterWaTemplates   = lazy(() => import("@/pages/master/MasterWaTemplates"));
const MasterLandingTexts       = lazy(() => import("@/pages/master/MasterLandingTexts"));
const MasterEmailMarketing     = lazy(() => import("@/pages/master/MasterEmailMarketing"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,         // 30s — evita refetch desnecessário ao navegar
      gcTime: 5 * 60_000,        // 5min — cache em memória
      retry: 2,                  // 2 retries em falhas transientes
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

/** Componente invisível que monitora a saúde do servidor */
const ServerHealthMonitor = () => {
  useServerHealth();
  return null;
};

/** Rastreia a página atual para o painel Live Users do master */
const LivePresenceTracker = () => {
  useLivePresence();
  return null;
};

/** Rotas do Painel Master — totalmente isoladas do sistema de tenant */
const MasterRoutes = () => {
  // Remove o overlay de carregamento HTML (que TenantContext normalmente removeria)
  useEffect(() => {
    (window as any).__removeHtmlLoading?.();
  }, []);

  return (
  <MasterAuthProvider>
    <ErrorBoundary>
    <Suspense fallback={null}>
    <Routes>
      <Route path="/master/login" element={<MasterLogin />} />
      <Route
        path="/master"
        element={
          <MasterRoute>
            <MasterLayout />
          </MasterRoute>
        }
      >
        <Route index element={<MasterDashboard />} />
        <Route path="condominios"   element={<MasterCondominios />} />
        <Route path="planos"        element={<MasterPlanos />} />
        <Route path="faturamento"   element={<MasterFaturamento />} />
        <Route path="moradores"     element={<MasterMoradores />} />
        <Route path="acessos"       element={<MasterAcessos />} />
        <Route path="relatorios"    element={<MasterRelatorios />} />
        <Route path="configuracoes" element={<MasterConfiguracoes />} />
        <Route path="whatsapp"       element={<MasterWhatsApp />} />
        <Route path="chatbot"        element={<MasterChatbot />} />
        <Route path="wa-templates"   element={<MasterWaTemplates />} />
        <Route path="leads"          element={<MasterLeads />} />
        <Route path="landing-texts"    element={<MasterLandingTexts />} />
        <Route path="email-marketing"  element={<MasterEmailMarketing />} />
      </Route>
    </Routes>
    </Suspense>
    </ErrorBoundary>
  </MasterAuthProvider>
  );
};

/**
 * Roteador interno que decide se mostra a landing page ou o app do condomínio.
 */
const AppRouter = () => {
  const { condominium, loading, error, isLandingPage, slug } = useTenant();

  // Consome o param ?admin_login= da URL e salva no localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get("admin_login");
    if (encoded) {
      try {
        const session = JSON.parse(atob(encoded));
        localStorage.setItem("unitSession", JSON.stringify(session));
        // Remove o param da URL sem recarregar a página
        params.delete("admin_login");
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
        window.history.replaceState({}, "", newUrl);
      } catch { /* param inválido, ignora */ }
    }
  }, []);

  // Carregando tenant — overlay HTML ainda está visível, não renderiza nada
  if (loading) return null;

  // Landing page (condoclick.com.br sem subdomínio) — sem health monitor
  if (isLandingPage) return (
    <>
      <LivePresenceTracker />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<AdminLogin />} />
          <Route path="/franqueado" element={<Franqueado />} />
          <Route path="/administradoras" element={<Administradoras />} />
          <Route path="/sindicos" element={<Sindicos />} />
          <Route path="/suporte" element={<Suporte />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/checkout/sucesso" element={<CheckoutSuccess />} />
          <Route path="/contrato/:token" element={<PublicContract />} />
          <Route path="/orcamento/:token" element={<PublicQuote />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/termos" element={<Termos />} />
          <Route path="/arquitetura-lgpd" element={<ArquiteturaLGPD />} />
          <Route path="/artigos" element={<BlogIndex lang="pt" />} />
          <Route path="/artigos/:slug" element={<BlogArticle lang="pt" />} />
          <Route path="/checklist-portaria" element={<ChecklistPortaria />} />
          <Route path="/livro-ocorrencias-condominio" element={<LivroOcorrencias />} />
          <Route path="/modelo-aviso-condominio" element={<ModeloAviso />} />
          <Route path="/encomendas" element={<Encomendas />} />
          <Route path="/dashboard-portaria" element={<DashboardPortaria />} />
          <Route path="/portaria" element={<Portaria />} />
          <Route path="/veiculos" element={<Veiculos />} />
          <Route path="/reservas" element={<Reservas />} />
          <Route path="/livro-digital" element={<LivroDigital />} />
          <Route path="/comunicados" element={<Comunicados />} />
          {/* SEO — Páginas pilar */}
          <Route path="/gestao-de-condominios" element={<GestaoCondominios />} />
          <Route path="/portaria-digital-condominio" element={<PortariaDigital />} />
          <Route path="/controle-encomendas-condominio" element={<ControleEncomendas />} />
          <Route path="/controle-visitantes-condominio" element={<ControleVisitantes />} />
          {/* SEO — Templates programáticos PT */}
          <Route path="/portaria-digital/:cidadeSlug" element={<CidadePage />} />
          <Route path="/como/:slug" element={<ProblemaPage />} />
          <Route path="/sistema-condominio/:slug" element={<TipoPage />} />
          <Route path="/comparar/:slug" element={<ComparacaoPage />} />
          {/* Rotas ES ─ mesmo componente; LanguageContext detecta /es* e força es-UY */}
          <Route path="/es" element={<LandingPage />} />
          <Route path="/es/franqueado" element={<Franqueado />} />
          <Route path="/es/administradoras" element={<Administradoras />} />
          <Route path="/es/sindicos" element={<Sindicos />} />
          <Route path="/es/soporte" element={<Suporte />} />
          <Route path="/es/contacto" element={<Contato />} />
          <Route path="/es/sobre" element={<Sobre />} />
          <Route path="/es/privacidad" element={<Privacidade />} />
          <Route path="/es/terminos" element={<Termos />} />
          <Route path="/es/arquitectura-lgpd" element={<ArquiteturaLGPD />} />
          <Route path="/es/checkout/exito" element={<CheckoutSuccess />} />
          <Route path="/es/articulos" element={<BlogIndex lang="es" />} />
          <Route path="/es/articulos/:slug" element={<BlogArticle lang="es" />} />
          {/* SEO — Páginas pilar ES */}
          <Route path="/es/gestion-condominios" element={<GestaoCondominiosES />} />
          <Route path="/es/porteria-digital-condominio" element={<PortariaDigitalES />} />
          <Route path="/es/control-encomiendas-condominio" element={<ControleEncomendasES />} />
          <Route path="/es/control-visitantes-condominio" element={<ControleVisitantesES />} />
          {/* SEO — Templates programáticos ES */}
          <Route path="/es/portaria-digital/:cidadeSlug" element={<CidadePageES />} />
          <Route path="/es/como/:slug" element={<ProblemaPageES />} />
          <Route path="/es/tipo-condominio/:slug" element={<TipoPageES />} />
          <Route path="/es/comparar/:slug" element={<ComparacaoPageES />} />
        </Routes>
      </Suspense>
    </>
  );

  // Condomínio não encontrado
  if (error || !condominium) return <TenantNotFound slug={slug} />;

  // App do condomínio — ativa monitoramento do servidor
  return (
    <ErrorBoundary>
      <LivePresenceTracker />
      <ServerHealthMonitor />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/pacotes" element={<MoradorPacotes />} />
          <Route path="/aprovar" element={<AprovarAcesso />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

const App = () => {
  // Detectar se estamos em rota /master para evitar carregar TenantContext
  const isMasterRoute = window.location.pathname.startsWith('/master');

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" storageKey="condoclick-theme" enableSystem={false}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          {isMasterRoute ? (
            <MasterRoutes />
          ) : (
            <>
              <LanguageProvider>
                <TenantProvider>
                  <AppRouter />
                </TenantProvider>
              </LanguageProvider>
            </>
          )}
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
